import React from 'react'
import cx from 'classnames'
import actions from 'redux/actions'
import { constants } from 'common/helpers'
import { FormattedMessage } from 'sb-react-intl'
import { useMemo, useState, useCallback } from 'sb-hooks'

import { Box } from 'components/_new/layout'
import Reply, { ReplyProps } from 'components/review/Reply/Reply'
import ProductTags from 'components/product/ProductTags/ProductTags'
import ReviewButton from 'components/review/ReviewButton/ReviewButton'

import Date from './Date/Date'
import Title from './Title/Title'
import Rating from './Rating/Rating'
import Helpful from '../Helpful/Helpful'
import UserInfo, { UserInfoProps } from '../UserInfo/UserInfo'
import ReplyForm from './ReplyForm/ReplyForm'
import UserSummary, { UserSummaryProps } from './UserSummary/UserSummary'
import Description from './Description/Description'
import ProductCard from './ProductCard/ProductCard'
import SkinAndAgeTypes, { SkinAndAgeTypesProps } from './SkinAndAgeTypes/SkinAndAgeTypes'
import LikesDislikes from './LikesDislikes/LikesDislikes'

import messages from './messages'
import s from './Review.scss'


export type ReviewType = SkinAndAgeTypesProps & {
  id: number,
  rating: number,
  date: string
  title?: string
  text: string
  tags: Array<{
    id: number
    name: string
    img: string
  }>
  user?: Partial<UserInfoProps> & {
    id: number
    stats?: UserSummaryProps['stats']
  }
  product?: {
    url: string
    name: string
    brand: string
    img: string
    image: string
  }
  replies?: Array<ReplyProps['reply']>
  dataTestId?: string
  isUseful?: boolean
  likeCount?: number
  dislikeCount?: number
  likes?: Review.LikeTag[]
  dislikes?: Review.LikeTag[]
}

type ReviewProps = {
  isLoggedIn?: boolean
  review: ReviewType,
  replies?: React.ReactNode
  starsCount?: number
  currentUserId?: number
  withMicrodata?: boolean
  isReplyButtonVisible?: boolean
  isHelpfulBlockVisible?: boolean
  isHelpfulButtonsActive?: boolean
  onVote?: (data: { reviewId: number, isUseful: boolean | null }) => void
  onReplyVote?: (data: { reviewId: number, replyId: number, isUseful: boolean | null }) => void
  onReplySubmit?: ({ message: string, reviewId: number }) => void
  onEditReviewClick?: () => void
  dataTestId?: string
}


const Review: React.FunctionComponent<ReviewProps> = (props) => {
  const {
    review, starsCount, currentUserId, withMicrodata, isHelpfulBlockVisible, onEditReviewClick,
    isLoggedIn, isReplyButtonVisible = true, isHelpfulButtonsActive = true, onReplyVote, onVote, onReplySubmit,
    dataTestId,
  } = props

  const {
    id, user, user: { id: authorId, stats } = {},
    rating, date, title, text, tags, isUseful, likeCount, dislikeCount, skinType, ageCategory,
    product: { img, image, url, brand, name } = {}, likes, dislikes, replies = [],
  } = review

  const [ isReplyFormShown, setReplyFormShown ] = useState(false)

  const handleReplyFormToggle = useCallback(({ isShown }) => {
    if (!isLoggedIn) {
      actions.models.modals.open(constants.modals.review.LoginRequired)
    }
    else {
      setReplyFormShown(isShown)
    }
  }, [ isLoggedIn ])

  const closeReplyForm = useCallback(() => setReplyFormShown(false), [])

  const handleReplySubmit = useCallback(({ message }) => {
    setReplyFormShown(false)
    onReplySubmit({ message, reviewId: id })
  }, [ id, onReplySubmit ])

  const hasAgeOrSkinType = useMemo(() => Boolean(skinType || ageCategory), [ skinType, ageCategory ])

  const isEditable = useMemo(() => (
    authorId && currentUserId && currentUserId === authorId
  ), [ authorId, currentUserId ])

  const isHelpfulVisible = useMemo(() => (
    (authorId && currentUserId && authorId !== currentUserId) || isHelpfulBlockVisible
  ), [ authorId, currentUserId, isHelpfulBlockVisible ])

  return (
    <div
      className={s.review}
      data-testid={dataTestId}
      {...withMicrodata && {
        itemProp: 'review',
        itemScope: true,
        itemType: 'http://schema.org/Review',
      }}
    >
      {
        Boolean(user) && (
          <div className={s.header}>
            <UserInfo
              url={user.url}
              avatar={user.avatar}
              gender={user.gender}
              username={user.username}
              firstName={user.firstName}
              lastName={user.lastName}
              withMicrodata={withMicrodata}
              avatarClassName={s.reviewAvatar}
              userNameClassName={s.userName}
            />
            {
              Boolean(withMicrodata && stats && stats.reviewsTotal) && (
                <UserSummary
                  url={user.url}
                  stats={stats}
                />
              )
            }
          </div>
        )
      }
      {
        Boolean(review.product) && (
          <div className={s.header}>
            <ProductCard {...{ img, image, url, brand, name }} />
          </div>
        )
      }
      <div className={s.body}>
        <div className={s.subHeader}>
          {
            Boolean(rating) && (
              <Rating {...{ withMicrodata, rating, starsCount }} />
            )
          }
          <Date {...{ withMicrodata, date }} />
          {
            isEditable && (
              <ReviewButton onEditReviewClick={onEditReviewClick}>
                <div className={cx(s.editReview, { [s.left]: starsCount === 10 })}>
                  <button type="button" className={s.edit}>
                    <FormattedMessage {...messages.edit} />
                  </button>
                </div>
              </ReviewButton>
            )
          }
        </div>
        {
          Boolean(likes && dislikes) && (
            <Box mt={32} mb={32} hideEmpty>
              <LikesDislikes
                likes={likes}
                dislikes={dislikes}
              />
            </Box>
          )
        }
        {
          hasAgeOrSkinType && (
            <Box mt={12}>
              <SkinAndAgeTypes
                ageCategory={ageCategory}
                skinType={skinType}
              />
            </Box>
          )
        }
        {
          Boolean(title) && (
            <Title {...{ withMicrodata, title }} />
          )
        }
        <Description {...{ withMicrodata, text }} />
        {
          Boolean(tags && tags.length) && (
            <ProductTags className={s.tags} items={tags} />
          )
        }
        {
          isHelpfulVisible && (
            <Helpful
              {...{
                id,
                likeCount,
                dislikeCount,
                isUseful,
                isReplyFormShown,
                isReplyButtonVisible,
                isHelpfulButtonsActive,
                onVote,
                onReplyFormToggle: handleReplyFormToggle,
                dataTestIds: {
                  likeButton: constants.testIds.review.likeButton,
                  dislikeButton: constants.testIds.review.dislikeButton,
                  replyButton: constants.testIds.review.replyButton,
                },
              }}
            />
          )
        }
        {
          isReplyFormShown && (
            <ReplyForm
              className={s.replyForm}
              onCancel={closeReplyForm}
              onSubmit={handleReplySubmit}
            />
          )
        }
        {
          Boolean(replies.length) && (
            <div className={s.replies}>
              {
                replies.map((reply) => (
                  <Reply
                    key={reply.id}
                    reply={reply}
                    reviewId={id}
                    currentUserId={currentUserId}
                    onVote={onReplyVote}
                  />
                ))
              }
            </div>
          )
        }
      </div>
    </div>
  )
}


export default Review
